import { ACTION, ACTION_SUBJECT, EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';

import type { AiAutocompletePlugin } from '../../aiAutocompletePluginType';
import { ACTIONS, type AiAutocompletePluginState } from '../types/types';
import { addAutocompleteViewedAnalytics } from '../utils/analytics';
import { callHeadingAutocompleteApi } from '../utils/api/headingAutocompleteApi';
import { getDocumentPlainText } from '../utils/charAndWordCount';
import { get1BasedLineIndexFromDocStart } from '../utils/docRegionCheck';
import { serializeDocWithCursorMarker } from '../utils/markdownSerializer';

import { buildSuggestionDecoration } from './decorations';
import { aiAutocompletePluginKey } from './plugin-key';

type PluginParams = {
	api: ExtractInjectionAPI<AiAutocompletePlugin> | undefined;
};

export const createPlugin = ({ api }: PluginParams): SafePlugin<AiAutocompletePluginState> => {
	let abortController: AbortController | null = null;
	const editorAnalyticsAPI = api?.analytics?.actions;

	return new SafePlugin({
		key: aiAutocompletePluginKey,
		// -----------------------------------------------------------------------
		// Plugin state — pure synchronous transitions
		// -----------------------------------------------------------------------
		state: {
			init(): AiAutocompletePluginState {
				return { status: 'idle' };
			},

			apply(tr, pluginState, _oldState, newState): AiAutocompletePluginState {
				const action = tr.getMeta(aiAutocompletePluginKey);
				if (action) {
					switch (action.type) {
						case ACTIONS.TRIGGER_MANUAL:
							return {
								status: 'loading',
								trigger: 'manual',
								cursorPos: action.cursorPos,
							};
						case ACTIONS.TRIGGER_AUTO:
							return {
								status: 'loading',
								trigger: 'auto',
								cursorPos: action.cursorPos,
								triggerType: action.triggerType,
								triggerLineIndex: get1BasedLineIndexFromDocStart(tr.doc, action.cursorPos),
								userPressedEnter: false,
								postEnterPlainTextBaseline: null,
							};
						case ACTIONS.SET_SUGGESTION:
							if (pluginState.status === 'loading') {
								if (pluginState.trigger === 'auto') {
									if (
										'displaySuggestionImmediately' in action &&
										action.displaySuggestionImmediately === true
									) {
										const cursorPos =
											'suggestionCursorPos' in action && action.suggestionCursorPos !== undefined
												? action.suggestionCursorPos
												: pluginState.cursorPos;
										return {
											status: 'suggestion',
											trigger: 'auto',
											cursorPos,
											suggestion: action.suggestion,
										};
									}
									return {
										status: 'suggestion_pending',
										trigger: 'auto',
										cursorPos: pluginState.cursorPos,
										suggestion: action.suggestion,
										triggerLineIndex: pluginState.triggerLineIndex,
									};
								}
								return {
									status: 'suggestion',
									trigger: 'manual',
									cursorPos: pluginState.cursorPos,
									suggestion: action.suggestion,
								};
							}
							return pluginState;
						case ACTIONS.REVEAL_SUGGESTION:
							if (pluginState.status === 'suggestion_pending') {
								const revealCursorPos =
									'cursorPos' in action && action.cursorPos !== undefined
										? action.cursorPos
										: pluginState.cursorPos;
								return {
									status: 'suggestion',
									trigger: 'auto',
									cursorPos: revealCursorPos,
									suggestion: pluginState.suggestion,
								};
							}
							return pluginState;
						case ACTIONS.ENTER_DURING_LOADING:
							if (pluginState.status === 'loading' && pluginState.trigger === 'auto') {
								return { ...pluginState, userPressedEnter: true };
							}
							return pluginState;
						case ACTIONS.DISMISS:
							return { status: 'idle' };
						default:
							return pluginState;
					}
				}

				// Structural doc change during auto-loading with Enter pressed:
				// capture a plain-text baseline so the API response handler can
				// verify the doc hasn't changed beyond the Enter-induced split.
				if (
					pluginState.status === 'loading' &&
					pluginState.trigger === 'auto' &&
					pluginState.userPressedEnter &&
					pluginState.postEnterPlainTextBaseline === null &&
					!tr.before.eq(tr.doc)
				) {
					return {
						...pluginState,
						postEnterPlainTextBaseline: getDocumentPlainText(newState.doc),
						cursorPos: tr.mapping.map(pluginState.cursorPos),
					};
				}

				if (tr.docChanged && pluginState.status !== 'idle') {
					const state = pluginState;
					return {
						...state,
						...(state.status === 'loading' ||
						state.status === 'suggestion' ||
						state.status === 'suggestion_pending'
							? { cursorPos: tr.mapping.map(state.cursorPos) }
							: {}),
					} as AiAutocompletePluginState;
				}

				return pluginState;
			},
		},

		// -----------------------------------------------------------------------
		// Decorations
		// -----------------------------------------------------------------------
		props: {
			decorations(state: EditorState) {
				const pluginState = aiAutocompletePluginKey.getState(state);
				if (pluginState?.status !== 'suggestion') {
					return null;
				}
				return buildSuggestionDecoration(state, pluginState);
			},

			handleDOMEvents: {
				click: (view, event) => {
					const { target } = event;
					if (
						target instanceof HTMLElement &&
						target.closest('[data-testid="heading-autocomplete-ghost-inline"]')
					) {
						event.preventDefault();

						const pluginState = aiAutocompletePluginKey.getState(view.state);
						if (pluginState?.status === 'suggestion') {
							api?.core?.actions?.execute?.(
								api?.aiAutocomplete?.commands?.acceptSuggestion?.({
									cursorPos: pluginState.cursorPos,
									suggestion: pluginState.suggestion,
								}),
							);
						}
						return true;
					}
					return false;
				},

				blur: (view) => {
					const pluginState = aiAutocompletePluginKey.getState(view.state);
					if (pluginState && pluginState.status !== 'idle') {
						api?.core?.actions?.execute?.(api?.aiAutocomplete?.commands?.dismissSuggestion?.());
					}
					return false;
				},
			},
		},

		// -----------------------------------------------------------------------
		// view() — owns all side effects (fetch lifecycle)
		// -----------------------------------------------------------------------
		view() {
			return {
				update(view, prevState) {
					const newPluginState = aiAutocompletePluginKey.getState(view.state);
					const prevPluginState = aiAutocompletePluginKey.getState(prevState);

					if (newPluginState === prevPluginState) {
						return;
					}

					// Leaving 'loading' — cancel in-flight fetch
					if (prevPluginState?.status === 'loading' && newPluginState?.status !== 'loading') {
						abortController?.abort();
						abortController = null;
					}

					// Entering 'loading' — fire the API call
					if (newPluginState?.status === 'loading' && prevPluginState?.status !== 'loading') {
						abortController = new AbortController();

						const markdown = serializeDocWithCursorMarker(view, newPluginState.cursorPos);
						const sectionType =
							newPluginState.trigger === 'auto' ? newPluginState.triggerType : undefined;

						callHeadingAutocompleteApi(markdown, sectionType, abortController.signal)
							.then((suggestion) => {
								const loading = aiAutocompletePluginKey.getState(view.state);
								if (loading?.status !== 'loading') {
									return;
								}

								const showImmediately = loading.trigger === 'auto' && loading.userPressedEnter;

								if (showImmediately && loading.postEnterPlainTextBaseline !== null) {
									const currentPlain = getDocumentPlainText(view.state.doc);
									if (currentPlain !== loading.postEnterPlainTextBaseline) {
										view.dispatch(
											view.state.tr.setMeta(aiAutocompletePluginKey, {
												type: ACTIONS.DISMISS,
											}),
										);
										return;
									}
								}

								const tr = view.state.tr.setMeta(aiAutocompletePluginKey, {
									type: ACTIONS.SET_SUGGESTION,
									suggestion,
									...(showImmediately
										? {
												displaySuggestionImmediately: true,
												suggestionCursorPos: view.state.selection.from,
											}
										: {}),
								});
								if (loading.trigger === 'manual' || showImmediately) {
									addAutocompleteViewedAnalytics(editorAnalyticsAPI)(tr);
								}
								view.dispatch(tr);
							})
							.catch((err: unknown) => {
								if (err instanceof Error && err.name === 'AbortError') {
									return;
								}
								const dismissTr = view.state.tr.setMeta(aiAutocompletePluginKey, {
									type: ACTIONS.DISMISS,
								});
								editorAnalyticsAPI?.attachAnalyticsEvent({
									action: ACTION.ERRORED,
									actionSubject: ACTION_SUBJECT.AI_AUTOCOMPLETE,
									eventType: EVENT_TYPE.OPERATIONAL,
									attributes: {
										errorMessage: err instanceof Error ? err.message : String(err),
									},
								})(dismissTr);
								view.dispatch(dismissTr);
							});
					}
				},

				destroy() {
					abortController?.abort();
					abortController = null;
				},
			};
		},

		// -----------------------------------------------------------------------
		// appendTransaction — doc change → dismiss (accept is handled in command)
		// -----------------------------------------------------------------------
		appendTransaction(transactions, _oldState, newState) {
			const currentPluginState = aiAutocompletePluginKey.getState(newState);

			// Visible suggestion or pending auto reveal: don't dismiss on doc
			// change — keydown already dismisses on typing before the doc mutates.
			if (
				currentPluginState?.status === 'suggestion' ||
				currentPluginState?.status === 'suggestion_pending'
			) {
				return null;
			}

			// Auto loading: allow doc changes (Enter / same-line edits).
			// Baseline capture is now handled in apply().
			if (currentPluginState?.status === 'loading' && currentPluginState.trigger === 'auto') {
				return null;
			}

			// Use structural doc equality, not `tr.docChanged`: the latter is true for any
			// step (including AnalyticsStep from attachAnalyticsEvent on invoke), which would
			// incorrectly dismiss loading state before the API runs.
			const documentChanged = transactions.some((tr) => !tr.before.eq(tr.doc));
			if (!documentChanged) {
				return null;
			}

			return newState.tr.setMeta(aiAutocompletePluginKey, { type: ACTIONS.DISMISS });
		},
	});
};
